import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type { RecipientConfigV1, TemplateOverrides } from '@/components/admin/notification-management/types';
import {
  ALWAYS_ON_NOTIFICATION_TYPES,
  CC_APPEND_ENABLED_TYPES,
  SETTINGS_PRIMARY_TYPES,
  canEditTemplate,
  validateRecipientConfig,
  validateTemplateOverrides
} from '@/components/admin/notification-management/config';
import type { SelfServiceEligibility } from '../../supabase/functions/_shared/review-request-policy';

const log = createComponentLogger('NotificationSettingsService', 'notification-settings');

function boolFromMetadata(value: unknown): boolean {
  return value === true || value === 'true';
}

function arrayFromMetadata(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry)).filter(Boolean);
  }
  if (typeof value === 'string' && value.trim()) {
    return value.split(',').map((entry) => entry.trim()).filter(Boolean);
  }
  return [];
}

function stringFromMetadata(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (Array.isArray(value)) {
    const values = value.map((entry) => String(entry).trim()).filter(Boolean);
    return values.length > 0 ? values.join(', ') : null;
  }
  return null;
}

function objectFromMetadata(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function loanNumberFromSubject(subject: string | null | undefined): string | null {
  const match = subject?.match(/\b(?:TEST-DRAFT-)?LW\d+\b/i);
  return match ? match[0] : null;
}

function loanNumbersFromCollection(value: unknown): string | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const numbers = value
    .map((entry) => {
      const item = objectFromMetadata(entry);
      return stringFromMetadata(item.loan_number)
        || stringFromMetadata(item.loanNumber)
        || stringFromMetadata(item.trans_details_loan_number);
    })
    .filter((loanNumber): loanNumber is string => Boolean(loanNumber));
  const unique = Array.from(new Set(numbers));
  return unique.length > 0 ? unique.join(', ') : null;
}

function borrowerNamesFromCollection(value: unknown): string | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const names = value
    .map((entry) => {
      const item = objectFromMetadata(entry);
      return stringFromMetadata(item.borrower_name) || stringFromMetadata(item.borrowerName);
    })
    .filter((borrowerName): borrowerName is string => Boolean(borrowerName));
  const unique = Array.from(new Set(names));
  return unique.length > 0 ? unique.join(', ') : null;
}

function sentAt(row: { delivered_at?: string | null; created_at?: string | null }): string | null {
  return row.delivered_at || row.created_at || null;
}

function curatedContext(metadata: Record<string, unknown>, subject?: string | null): SendAttempt['context'] {
  const data = objectFromMetadata(metadata.data);
  return {
    loan_number: stringFromMetadata(metadata.loan_number)
      || stringFromMetadata(metadata.loanNumber)
      || stringFromMetadata(metadata.loan_numbers)
      || stringFromMetadata(data.loan_number)
      || stringFromMetadata(data.loanNumber)
      || stringFromMetadata(data.loan_numbers)
      || loanNumbersFromCollection(data.loans)
      || loanNumberFromSubject(subject),
    encompass_loan_id: stringFromMetadata(metadata.encompass_loan_id)
      || stringFromMetadata(metadata.loan_id)
      || stringFromMetadata(metadata.loanId)
      || stringFromMetadata(data.encompass_loan_id)
      || stringFromMetadata(data.encompassLoanId)
      || stringFromMetadata(data.loan_id)
      || stringFromMetadata(data.loanId),
    borrower_name: stringFromMetadata(metadata.borrower_name)
      || stringFromMetadata(metadata.borrowerName)
      || stringFromMetadata(data.borrower_name)
      || stringFromMetadata(data.borrowerName)
      || borrowerNamesFromCollection(data.loans),
    recipient_role: stringFromMetadata(metadata.recipient_role)
      || stringFromMetadata(metadata.assignee_role)
      || stringFromMetadata(data.recipient_role)
      || stringFromMetadata(data.recipientRole)
      || stringFromMetadata(data.assignee_role)
      || stringFromMetadata(data.assigneeRole),
    channel: stringFromMetadata(metadata.channel) || stringFromMetadata(data.channel),
    is_test: boolFromMetadata(metadata.is_test),
    resend_of: stringFromMetadata(metadata.resend_of)
  };
}

interface InvokeErrorLike {
  message?: string;
  context?: { status?: number; json?: () => Promise<unknown> };
}

/**
 * supabase.functions.invoke() surfaces a non-2xx response as an error whose `context` is
 * the raw Response. Pull the edge function's `{ error, message }` body out of it so the UI
 * can show the server's reason instead of "non-2xx status code".
 */
async function parseInvokeErrorBody(error: unknown): Promise<{ status: number; code: string; message: string; retryAfter: string | null }> {
  const err = (error ?? {}) as InvokeErrorLike;
  const fallback = err.message || 'Request failed';
  const ctx = err.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = (await ctx.json()) as { error?: unknown; message?: unknown; retryAfter?: unknown } | null;
      const code = typeof body?.error === 'string' ? body.error : 'request_failed';
      const message = typeof body?.message === 'string'
        ? body.message
        : (typeof body?.error === 'string' ? body.error : fallback);
      const retryAfter = typeof body?.retryAfter === 'string' ? body.retryAfter : null;
      return { status: ctx.status ?? 500, code, message, retryAfter };
    } catch {
      // Unreadable body: fall through to the transport message.
    }
  }
  return { status: 0, code: 'request_failed', message: fallback, retryAfter: null };
}

/** Admin actions take one notification type; self-service actions take a catalog category. */
function activityRequestScope(notificationType: string, source: ActivitySource): Record<string, unknown> {
  return source.scope === 'mine'
    ? { categoryId: source.categoryId }
    : { notificationType };
}

function toSendAttempt(row: {
  id: string;
  created_at?: string | null;
  delivered_at?: string | null;
  subject?: string | null;
  status?: string | null;
  email_address?: string | null;
  content?: string | null;
  metadata?: Record<string, unknown> | null;
}): SendAttempt {
  const metadata = row.metadata || {};
  return {
    id: row.id,
    notificationType: String(metadata.notification_type || ''),
    subject: row.subject ?? null,
    status: row.status ?? null,
    sentAt: sentAt(row),
    toEmails: arrayFromMetadata(metadata.to_emails || metadata.recipient_emails || metadata.to || metadata.toRecipients || row.email_address),
    ccEmails: arrayFromMetadata(metadata.cc_emails || metadata.cc || metadata.ccRecipients || metadata.cc_recipients),
    resendId: typeof metadata.resend_id === 'string' ? metadata.resend_id : null,
    hasStoredHtml: Boolean(row.content),
    context: curatedContext(metadata, row.subject)
  };
}

function activityStatusKey(status: string | null | undefined): 'delivered' | 'opened' | 'bounced' | 'failed' | 'other' {
  const normalized = (status || 'sent').toLowerCase();
  if (normalized === 'sent' || normalized === 'delivered') {
    return 'delivered';
  }
  if (normalized === 'opened') {
    return 'opened';
  }
  if (normalized === 'bounced') {
    return 'bounced';
  }
  if (normalized === 'failed' || normalized === 'complained') {
    return 'failed';
  }
  return 'other';
}

function buildActivityStats(rows: SendAttempt[]): ActivityStats {
  const stats: ActivityStats = {
    total: rows.length,
    delivered: 0,
    opened: 0,
    bounced: 0,
    failed: 0,
    openRate: null
  };

  for (const row of rows) {
    const status = activityStatusKey(row.status);
    if (status === 'delivered') {
      stats.delivered += 1;
    }
    if (status === 'opened') {
      stats.delivered += 1;
      stats.opened += 1;
    }
    if (status === 'bounced') {
      stats.bounced += 1;
    }
    if (status === 'failed') {
      stats.failed += 1;
    }
  }

  stats.openRate = stats.opened > 0 && stats.delivered > 0
    ? Math.round((stats.opened / stats.delivered) * 100)
    : null;

  return stats;
}

/**
 * Notification Settings Service
 *
 * Manages notification toggle state and provides access to email previews.
 * Works with the notification_settings table and notification-preview Edge Function.
 */

export interface NotificationSetting {
  id: string;
  notification_type: string;
  name: string;
  description: string | null;
  category: string;
  is_enabled: boolean;
  recipients: string;
  additional_recipient_ids: string[];
  additional_emails: string[];
  recipient_config: RecipientConfigV1 | null;
  template_subject_override: string | null;
  template_body_override: string | null;
  template_accent_color: string | null;
  template_updated_at: string | null;
  template_updated_by: string | null;
  trigger_description: string;
  updated_at: string;
  updated_by: string | null;
}

export interface AdditionalRecipient {
  id: string;
  email: string;
  full_name: string;
  first_name: string | null;
  last_name: string | null;
  roles?: string[] | null;
}

export interface EncompassRoleOption {
  organization_id: string;
  role_id: number;
  role_name: string;
  abbreviation: string | null;
  assignment_source: 'designated' | 'fixed_milestone' | 'milestone_free' | 'milestone';
  legacy_slug: string | null;
  is_selectable: boolean;
  last_seen_at: string | null;
}

export interface NotificationPreview {
  type: string;
  name: string;
  description: string;
  category: string;
  recipients: string;
  triggerDescription: string;
  subject: string;
  html: string;
  sampleData: Record<string, unknown>;
}

export interface NotificationTemplateMetadata {
  type: string;
  name: string;
  description: string;
  category: string;
  recipients: string;
  triggerDescription: string;
  templateEditable?: boolean;
  recipientEditable?: boolean;
}

export interface SendCountSummary {
  /**
   * Sent attempts in the last 30 days. This keeps the existing detail/activity badges stable.
   */
  count: number;
  /**
   * All-time sent attempts. This powers the compact master-list count.
   */
  countAllTime: number;
  lastSent: string | null;
}

/** Where an Activity request is scoped: the admin tab (one notification type, whole org) or the user's own loans (a catalog category). */
export type ActivityScope = 'admin' | 'mine';

export interface ActivitySource {
  scope?: ActivityScope;
  /** Required when scope is 'mine'. */
  categoryId?: string;
}

export interface DeliveryStatus {
  state: 'delivered' | 'bounced' | 'complained' | 'pending' | 'unknown';
  at: string | null;
}

/** Loan-team resend eligibility as the server will enforce it (self-service scope only). */
export type ResendEligibility = SelfServiceEligibility;

export interface SendAttempt {
  id: string;
  organizationId?: string | null;
  notificationType: string;
  subject: string | null;
  status: string | null;
  sentAt: string | null;
  toEmails: string[];
  ccEmails: string[];
  resendId: string | null;
  hasStoredHtml: boolean;
  /** Provider-confirmed delivery from the Resend ledger; null/undefined when the function did not attach one. */
  delivery?: DeliveryStatus | null;
  /** Self-service scope: whether this recipient can be resent right now, from the ledger. */
  resend?: ResendEligibility | null;
  auth?: {
    verificationStatus: string; verifiedAt: string | null; trackingAvailable: boolean;
    recipients: Array<{email:string;role:string;state:string;openedAt:string|null;clickedAt:string|null;events:Array<{type:string;at:string}>}>;
  };
  context: {
    recruit_id?: string | null;
    nmls_number?: string | null;
    purpose?: string | null;
    loan_number?: string | null;
    encompass_loan_id?: string | null;
    borrower_name?: string | null;
    recipient_role?: string | null;
    channel?: string | null;
    is_test?: boolean;
    /** communications.id of the email this row repeats (admin resend from Activity). */
    resend_of?: string | null;
  };
}

export type ReviewRequestResendResult =
  | {
      ok: true;
      recipient: string;
      role: string | null;
      resendId: string | null;
      /** Bookkeeping that failed after the provider accepted the email (see send-review-request). */
      warnings: string[];
    }
  | { ok: false; code: string; message: string; retryAfter?: string | null };

export interface ActivityFilters extends ActivitySource {
  page?: number;
  pageSize?: number;
  q?: string;
  status?: string;
  dateRange?: string;
  role?: string;
  includeTests?: boolean;
}

export interface ActivityStats {
  total: number;
  delivered: number;
  opened: number;
  bounced: number;
  failed: number;
  openRate: number | null;
  verified?: number;
}

export interface ActivityPage {
  rows: SendAttempt[];
  total: number;
  stats: ActivityStats;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ActivityCsvExport {
  csv: string;
  filename: string;
  total: number;
}

export interface StoredEmail {
  id: string;
  subject: string | null;
  html: string;
  toEmails: string[];
  ccEmails: string[];
  sentAt: string | null;
  status: string | null;
  context: SendAttempt['context'];
}

class NotificationSettingsService {
  /**
   * Fetch all notification settings from the database
   * Requires admin role (enforced by RLS)
   */
  async getSettings(): Promise<NotificationSetting[]> {
    try {
      log.info('Fetching notification settings');

      const { data, error } = await supabase
        .from('notification_settings')
        .select('*')
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) {
        log.error('Failed to fetch notification settings', { error: error.message }, error);
        throw new Error(`Failed to fetch notification settings: ${error.message}`);
      }

      log.info('Notification settings fetched', { count: data?.length || 0 });
      return data || [];
    } catch (error) {
      log.error('Error in getSettings', {
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      throw error;
    }
  }

  /**
   * Toggle a notification's enabled state
   * Requires admin role (enforced by RLS)
   */
  async toggleEnabled(notificationType: string, enabled: boolean): Promise<NotificationSetting> {
    // Mandatory types have no off switch; the database refuses is_enabled = false.
    if (!enabled && ALWAYS_ON_NOTIFICATION_TYPES.has(notificationType)) {
      log.warn('Refused to disable an always-on notification', { metadata: { notificationType } });
      throw new Error(`${notificationType} is always on and cannot be disabled.`);
    }
    try {
      log.info('Toggling notification', { notificationType, enabled });

      // Get current user for audit
      const { data: { user } } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from('notification_settings')
        .update({
          is_enabled: enabled,
          updated_by: user?.id || null
        })
        .eq('notification_type', notificationType)
        .select()
        .single();

      if (error) {
        log.error('Failed to toggle notification', {
          notificationType,
          enabled,
          error: error.message
        }, error);
        throw new Error(`Failed to toggle notification: ${error.message}`);
      }

      log.info('Notification toggled successfully', { notificationType, enabled });
      return data;
    } catch (error) {
      log.error('Error in toggleEnabled', {
        notificationType,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      throw error;
    }
  }

  /**
   * Get a single notification's settings
   */
  async getSetting(notificationType: string): Promise<NotificationSetting | null> {
    try {
      const { data, error } = await supabase
        .from('notification_settings')
        .select('*')
        .eq('notification_type', notificationType)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Not found
        }
        throw new Error(`Failed to fetch setting: ${error.message}`);
      }

      return data;
    } catch (error) {
      log.error('Error in getSetting', {
        notificationType,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      throw error;
    }
  }

  /**
   * Fetch email preview for a specific notification type
   * Uses the notification-preview Edge Function
   */
  async getPreview(notificationType: string, draftOverrides?: TemplateOverrides): Promise<NotificationPreview> {
    try {
      log.info('Fetching notification preview', { notificationType });

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const hasDraft = Boolean(
        draftOverrides
        && (draftOverrides.subject !== undefined
          || draftOverrides.body !== undefined)
      );

      if (hasDraft) {
        const validationErrors = validateTemplateOverrides(notificationType, draftOverrides);
        if (validationErrors.length > 0) {
          throw new Error(validationErrors.join(' '));
        }
      }

      const previewUrl = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notification-preview`);
      previewUrl.searchParams.set('type', notificationType);
      previewUrl.searchParams.set('_', String(Date.now()));

      const response = await fetch(previewUrl.toString(), {
        method: hasDraft ? 'POST' : 'GET',
        cache: 'no-store',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: hasDraft ? JSON.stringify({ type: notificationType, overrides: draftOverrides }) : undefined
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch preview');
      }

      const previewData = await response.json();
      log.info('Preview fetched successfully', { notificationType });
      return previewData;
    } catch (error) {
      log.error('Error fetching preview', {
        notificationType,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      throw error;
    }
  }

  /**
   * Fetch all template metadata (without HTML)
   */
  async getTemplateMetadata(): Promise<NotificationTemplateMetadata[]> {
    try {
      log.info('Fetching template metadata');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notification-preview`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch template metadata');
      }

      const { templates } = await response.json();
      log.info('Template metadata fetched', { count: templates?.length || 0 });
      return templates || [];
    } catch (error) {
      log.error('Error fetching template metadata', {
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      throw error;
    }
  }

  /**
   * Send a test notification (bypasses is_enabled check)
   */
  async sendTestNotification(
    notificationType: string,
    userId: string
  ): Promise<{ success: boolean; emailId?: string; message: string }> {
    try {
      log.info('Sending test notification', { notificationType, userId });

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Goodbye Letter has its own edge function (PDF attachment + sample
      // data path) — route the test send there with the admin's email as
      // the override.
      if (notificationType === 'servicing_goodbye_letter') {
        const { data: profile, error: profileError } = await supabase
          .from('users')
          .select('email')
          .eq('id', userId)
          .maybeSingle();
        if (profileError || !profile?.email) {
          return { success: false, message: 'Could not resolve admin email for test send' };
        }

        const { data, error } = await supabase.functions.invoke('send-goodbye-letter', {
          body: {
            triggeredBy: 'manual_admin',
            isTest: true,
            testEmailOverride: profile.email
          },
          headers: { Authorization: `Bearer ${session.access_token}` }
        });

        if (error) {
          return { success: false, message: error.message };
        }
        return {
          success: data?.success !== false,
          emailId: data?.resendId,
          message: data?.success === false
            ? (data.error || 'Goodbye letter test send failed')
            : `Test goodbye letter sent to ${profile.email}`
        };
      }

      const { data, error } = await supabase.functions.invoke('send-notification', {
        body: {
          userId,
          notificationType,
          isTest: true, // Bypasses is_enabled check
          // Tests go ONLY to the admin who clicked — never to the configured
          // additional recipients (send-notification skips its settings-CC
          // resolution entirely when this flag is set).
          skipAdditionalRecipientsFromSettings: true,
          data: {} // Use sample data from template
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        log.error('Test notification failed', {
          notificationType,
          error: error.message
        }, error);
        return {
          success: false,
          message: error.message
        };
      }

      log.info('Test notification sent', {
        notificationType,
        emailId: data?.emailId
      });

      return {
        success: true,
        emailId: data?.emailId,
        message: data?.message || 'Test notification sent successfully'
      };
    } catch (error) {
      log.error('Error sending test notification', {
        notificationType,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Fetch available users for adding as additional recipients
   * Returns active users within the organization
   */
  async getAvailableUsers(): Promise<AdditionalRecipient[]> {
    try {
      log.info('Fetching available users for additional recipients');

      // Get the current user's organization
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      const { data: currentProfile, error: profileError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (profileError || !currentProfile) {
        throw new Error('User profile not found');
      }

      // Fetch all active users in the organization
      const { data, error } = await supabase
        .from('users')
        .select('id, email, first_name, last_name, full_name, roles')
        .eq('organization_id', currentProfile.organization_id)
        .eq('employment_status', 'active')
        .order('full_name');

      if (error) {
        throw new Error(`Failed to fetch users: ${error.message}`);
      }

      const users = (data || []).map(user => ({
        id: user.id,
        email: user.email,
        full_name: user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        roles: Array.isArray(user.roles) ? user.roles : []
      }));

      log.info('Available users fetched', { count: users.length });
      return users;
    } catch (error) {
      log.error('Error in getAvailableUsers', {
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      throw error;
    }
  }

  /**
   * Get additional recipient details for a notification type
   */
  async getAdditionalRecipientDetails(notificationType: string): Promise<AdditionalRecipient[]> {
    try {
      // Get the setting to find additional_recipient_ids
      const setting = await this.getSetting(notificationType);
      if (!setting || !setting.additional_recipient_ids?.length) {
        return [];
      }

      // Fetch user details for all IDs
      const { data, error } = await supabase
        .from('users')
        .select('id, email, first_name, last_name, full_name')
        .in('id', setting.additional_recipient_ids)
        .eq('employment_status', 'active');

      if (error) {
        log.error('Failed to fetch additional recipients', { error: error.message }, error);
        throw new Error(`Failed to fetch additional recipients: ${error.message}`);
      }

      return (data || []).map(user => ({
        id: user.id,
        email: user.email,
        full_name: user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
        first_name: user.first_name,
        last_name: user.last_name
      }));
    } catch (error) {
      log.error('Error in getAdditionalRecipientDetails', {
        notificationType,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      throw error;
    }
  }

  /**
   * Update additional recipients for a notification type (bulk replace)
   */
  async updateAdditionalRecipients(notificationType: string, userIds: string[]): Promise<NotificationSetting> {
    try {
      log.info('Updating additional recipients', { notificationType, count: userIds.length });

      // Get current user for audit
      const { data: { user } } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from('notification_settings')
        .update({
          additional_recipient_ids: userIds,
          updated_by: user?.id || null
        })
        .eq('notification_type', notificationType)
        .select()
        .single();

      if (error) {
        log.error('Failed to update additional recipients', {
          notificationType,
          error: error.message
        }, error);
        throw new Error(`Failed to update additional recipients: ${error.message}`);
      }

      log.info('Additional recipients updated successfully', { notificationType, count: userIds.length });
      return data;
    } catch (error) {
      log.error('Error in updateAdditionalRecipients', {
        notificationType,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      throw error;
    }
  }

  async updateRecipientConfig(notificationType: string, config: RecipientConfigV1 | null): Promise<NotificationSetting> {
    try {
      const validationErrors = validateRecipientConfig(notificationType, config);
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' '));
      }

      const { data: { user } } = await supabase.auth.getUser();
      const updatePayload: {
        recipient_config: RecipientConfigV1 | null;
        updated_by: string | null;
        additional_recipient_ids?: string[];
        additional_emails?: string[];
      } = {
        recipient_config: config,
        updated_by: user?.id || null
      };

      if (!config) {
        // A reset to code-owned routing must also remove the legacy arrays.
        // Otherwise effectiveRecipientConfigFor() and legacy-aware senders
        // immediately resurrect the hidden recipients after recipient_config
        // is cleared.
        updatePayload.additional_recipient_ids = [];
        updatePayload.additional_emails = [];
      } else if (CC_APPEND_ENABLED_TYPES.has(notificationType) || SETTINGS_PRIMARY_TYPES.has(notificationType)) {
        const legacySource = SETTINGS_PRIMARY_TYPES.has(notificationType) ? config.to : config.cc;
        const additionalRecipientIds: string[] = [];
        const additionalEmails: string[] = [];
        for (const target of legacySource) {
          if (target.kind === 'user') {
            additionalRecipientIds.push(target.user_id);
          } else if (target.kind === 'raw_email') {
            additionalEmails.push(target.email.trim().toLowerCase());
          }
        }
        updatePayload.additional_recipient_ids = additionalRecipientIds;
        updatePayload.additional_emails = additionalEmails;
      }

      const { data, error } = await supabase
        .from('notification_settings')
        .update(updatePayload)
        .eq('notification_type', notificationType)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update recipient configuration: ${error.message}`);
      }

      return data;
    } catch (error) {
      log.error('Error in updateRecipientConfig', {
        notificationType,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      throw error;
    }
  }

  async updateOverrides(notificationType: string, overrides: TemplateOverrides): Promise<NotificationSetting> {
    try {
      if (!canEditTemplate(notificationType)) {
        throw new Error('Template overrides are not editable for this notification.');
      }

      const validationErrors = validateTemplateOverrides(notificationType, overrides);
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' '));
      }

      const { data: { user } } = await supabase.auth.getUser();
      const updatePayload: Record<string, string | null> = {
        template_updated_at: new Date().toISOString(),
        template_updated_by: user?.id || null,
        updated_by: user?.id || null
      };

      updatePayload.template_subject_override = overrides.subject?.trim() || null;
      updatePayload.template_body_override = overrides.body?.trim() || null;

      const { data, error } = await supabase
        .from('notification_settings')
        .update(updatePayload)
        .eq('notification_type', notificationType)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update template overrides: ${error.message}`);
      }

      return data;
    } catch (error) {
      log.error('Error in updateOverrides', {
        notificationType,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      throw error;
    }
  }

  async getDistinctUserRoles(): Promise<string[]> {
    const users = await this.getAvailableUsers();
    const ids = users.map((u) => u.id);
    if (ids.length === 0) {
      return [];
    }

    const { data, error } = await supabase
      .from('users')
      .select('roles')
      .in('id', ids);

    if (error) {
      throw new Error(`Failed to fetch user roles: ${error.message}`);
    }

    return Array.from(new Set(
      (data || [])
        .flatMap((row: { roles: string[] | null }) => row.roles || [])
        .filter((role): role is string => Boolean(role))
    )).sort((a, b) => a.localeCompare(b));
  }

  /**
   * Fetch the Encompass role catalog visible to the current administrator.
   * The table grants authenticated SELECT but its RLS policy returns rows only
   * to active admins in the same organization.
   */
  async getEncompassRoles(): Promise<EncompassRoleOption[]> {
    const { data, error } = await supabase
      .from('encompass_role_catalog')
      .select(`
        organization_id,
        role_id,
        role_name,
        abbreviation,
        assignment_source,
        legacy_slug,
        is_selectable,
        last_seen_at
      `)
      .eq('is_selectable', true)
      .order('role_name');

    if (error) {
      throw new Error(`Failed to fetch Encompass roles: ${error.message}`);
    }

    return (data || []) as EncompassRoleOption[];
  }

  async getSendCountSummaries(): Promise<Record<string, SendCountSummary>> {
    try {
      const { data, error } = await supabase.functions.invoke('notification-activity', {
        body: { action: 'counts' }
      });
      if (error) {
        throw new Error(error.message);
      }
      if (data?.error) {
        throw new Error(data.error);
      }
      return data?.counts || {};
    } catch (error) {
      log.error('Unable to load notification send counts', {
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      throw error;
    }
  }

  async getRecentSends(notificationType: string, limit = 10, includeTests = false): Promise<SendAttempt[]> {
    try {
      const page = await this.getActivityPage(notificationType, {
        page: 1,
        pageSize: limit,
        includeTests
      });
      return page.rows;
    } catch (error) {
      log.error('Unable to load notification recent sends', {
        notificationType,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      throw error;
    }
  }

  async getActivityPage(notificationType: string, filters: ActivityFilters = {}): Promise<ActivityPage> {
    try {
      const { data, error } = await supabase.functions.invoke('notification-activity', {
        body: {
          action: filters.scope === 'mine' ? 'my_recent' : 'recent',
          ...activityRequestScope(notificationType, filters),
          page: filters.page || 1,
          pageSize: filters.pageSize || 50,
          limit: filters.pageSize || 50,
          q: filters.q || '',
          status: filters.status || 'all',
          dateRange: filters.dateRange || '30',
          role: filters.role || 'all',
          includeTests: Boolean(filters.includeTests)
        }
      });
      if (error) {
        throw new Error(error.message);
      }
      if (data?.error) {
        throw new Error(data.error);
      }
      const rows = (data?.rows || data?.attempts || []) as SendAttempt[];
      const fallbackStats = buildActivityStats(rows);
      const total = typeof data?.total === 'number' ? Number(data.total) : rows.length;
      return {
        rows,
        total,
        stats: data?.stats || fallbackStats,
        page: Number(data?.page || filters.page || 1),
        pageSize: Number(data?.pageSize || filters.pageSize || 50),
        totalPages: Number(data?.totalPages || Math.max(1, Math.ceil(total / (filters.pageSize || 50))))
      };
    } catch (error) {
      log.error('Unable to load notification activity page', {
        notificationType,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      throw error;
    }
  }

  async exportActivityCsv(notificationType: string, filters: Omit<ActivityFilters, 'page' | 'pageSize'> = {}): Promise<ActivityCsvExport> {
    try {
      const { data, error } = await supabase.functions.invoke('notification-activity', {
        body: {
          action: filters.scope === 'mine' ? 'my_export' : 'export',
          ...activityRequestScope(notificationType, filters),
          q: filters.q || '',
          status: filters.status || 'all',
          dateRange: filters.dateRange || '30',
          role: filters.role || 'all',
          includeTests: Boolean(filters.includeTests)
        }
      });
      if (error) {
        throw new Error(error.message);
      }
      if (data?.error) {
        throw new Error(data.error);
      }
      return {
        csv: String(data?.csv || ''),
        filename: String(data?.filename || `${notificationType}-activity.csv`),
        total: Number(data?.total || 0)
      };
    } catch (error) {
      log.error('Unable to export notification activity CSV', {
        notificationType,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      throw error;
    }
  }

  async getStoredEmail(communicationId: string, notificationType: string, source: ActivitySource = {}): Promise<StoredEmail> {
    try {
      const { data, error } = await supabase.functions.invoke('notification-activity', {
        body: {
          action: source.scope === 'mine' ? 'my_email' : 'email',
          communicationId,
          ...activityRequestScope(notificationType, source)
        }
      });
      if (error) {
        throw new Error(error.message);
      }
      if (data?.error) {
        throw new Error(data.error);
      }
      return data?.email;
    } catch (error) {
      log.error('Unable to load stored notification email', {
        communicationId,
        notificationType,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      throw error;
    }
  }

  /**
   * Send one earlier Google review request again, to the same recipient, with the current
   * template. Admins use mode 'resend'; the user-side page uses 'resend_loan_team', which
   * send-review-request limits to the caller's own loans (with cooldown and attempt caps).
   * Either way the function validates the row, re-claims the ledger entry and logs the new
   * email with `resend_of` / `resent_by` / `resend_actor_role`.
   */
  async resendRecruitCode(communicationId: string): Promise<ReviewRequestResendResult> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
return {ok:false,code:'unauthorized',message:'Please sign in again.'};
}
      const response = await fetch('/.netlify/functions/recruit-auth-admin', {method:'POST',headers:{'Content-Type':'application/json',Authorization:`Bearer ${session.access_token}`},body:JSON.stringify({action:'resend',communication_id:communicationId})});
      const result = await response.json();
      return response.ok && result.success ? {ok:true,recipient:'the saved recruit email',role:null,resendId:null,warnings:[]}
        : {ok:false,code:result.error ?? 'unavailable',message:result.message ?? 'Unable to request a fresh code.'};
    } catch {
return {ok:false,code:'unavailable',message:'Unable to request a fresh code. Check Activity before trying again.'};
}
  }

  async resendReviewRequest(communicationId: string, source: ActivitySource = {}): Promise<ReviewRequestResendResult> {
    try {
      const { data, error } = await supabase.functions.invoke('send-review-request', {
        body: { mode: source.scope === 'mine' ? 'resend_loan_team' : 'resend', communicationId }
      });
      if (error) {
        const refusal = await parseInvokeErrorBody(error);
        log.warn('Review request resend refused', {
          metadata: { communicationId, status: refusal.status, code: refusal.code }
        });
        return { ok: false, code: refusal.code, message: refusal.message, retryAfter: refusal.retryAfter };
      }
      if (!data?.resent) {
        return {
          ok: false,
          code: typeof data?.error === 'string' ? data.error : 'unknown',
          message: typeof data?.message === 'string' ? data.message : 'The resend did not complete.'
        };
      }
      log.info('Review request resent', { metadata: { communicationId, resendId: data.resendId ?? null } });
      return {
        ok: true,
        recipient: typeof data.recipient === 'string' ? data.recipient : '',
        role: typeof data.role === 'string' ? data.role : null,
        resendId: typeof data.resendId === 'string' ? data.resendId : null,
        warnings: Array.isArray(data.warnings) ? data.warnings.filter((w: unknown): w is string => typeof w === 'string') : []
      };
    } catch (error) {
      log.error('Error resending review request', {
        metadata: { communicationId, error: error instanceof Error ? error.message : 'Unknown error' }
      }, error as Error);
      return {
        ok: false,
        code: 'unexpected',
        message: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  private async getSendCountsFromCommunications(): Promise<Record<string, SendCountSummary>> {
    const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const baseSelect = 'created_at, delivered_at, metadata';
    const [allRows, deliveredRows, createdFallbackRows] = await Promise.all([
      supabase
        .from('communications')
        .select(baseSelect)
        .eq('type', 'email')
        .eq('direction', 'outbound')
        .eq('status', 'sent'),
      supabase
        .from('communications')
        .select(baseSelect)
        .eq('type', 'email')
        .eq('direction', 'outbound')
        .eq('status', 'sent')
        .gte('delivered_at', cutoff),
      supabase
        .from('communications')
        .select(baseSelect)
        .eq('type', 'email')
        .eq('direction', 'outbound')
        .eq('status', 'sent')
        .is('delivered_at', null)
        .gte('created_at', cutoff)
    ]);

    if (allRows.error) {
      throw new Error(`Failed to load send counts: ${allRows.error.message}`);
    }
    if (deliveredRows.error) {
      throw new Error(`Failed to load send counts: ${deliveredRows.error.message}`);
    }
    if (createdFallbackRows.error) {
      throw new Error(`Failed to load send counts: ${createdFallbackRows.error.message}`);
    }

    const counts: Record<string, SendCountSummary> = {};
    for (const row of allRows.data || []) {
      const metadata = (row.metadata || {}) as Record<string, unknown>;
      if (boolFromMetadata(metadata.is_test)) {
        continue;
      }
      const notificationType = typeof metadata.notification_type === 'string' ? metadata.notification_type : null;
      if (!notificationType) {
        continue;
      }
      const timestamp = sentAt(row);
      counts[notificationType] = counts[notificationType] || { count: 0, countAllTime: 0, lastSent: null };
      counts[notificationType].countAllTime += 1;
      if (timestamp && (!counts[notificationType].lastSent || timestamp > counts[notificationType].lastSent)) {
        counts[notificationType].lastSent = timestamp;
      }
    }

    for (const row of [...(deliveredRows.data || []), ...(createdFallbackRows.data || [])]) {
      const metadata = (row.metadata || {}) as Record<string, unknown>;
      if (boolFromMetadata(metadata.is_test)) {
        continue;
      }
      const notificationType = typeof metadata.notification_type === 'string' ? metadata.notification_type : null;
      if (!notificationType) {
        continue;
      }
      counts[notificationType] = counts[notificationType] || { count: 0, countAllTime: 0, lastSent: null };
      counts[notificationType].count += 1;
    }

    return counts;
  }

  private async getRecentSendsFromCommunications(notificationType: string, limit: number, includeTests: boolean): Promise<SendAttempt[]> {
    const { data, error } = await supabase
      .from('communications')
      .select('id, created_at, delivered_at, subject, status, email_address, content, metadata')
      .eq('type', 'email')
      .eq('direction', 'outbound')
      .eq('status', 'sent')
      .filter('metadata->>notification_type', 'eq', notificationType)
      .order('delivered_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })
      .limit(Math.min(Math.max(limit * (includeTests ? 1 : 5), limit), 100));

    if (error) {
      throw new Error(`Failed to load recent sends: ${error.message}`);
    }

    return (data || [])
      .map((row) => toSendAttempt(row))
      .filter((attempt) => includeTests || !attempt.context.is_test)
      .slice(0, limit);
  }

  private async getStoredEmailFromCommunications(communicationId: string, notificationType: string): Promise<StoredEmail> {
    const { data, error } = await supabase
      .from('communications')
      .select('id, created_at, delivered_at, subject, status, email_address, content, metadata')
      .eq('type', 'email')
      .eq('direction', 'outbound')
      .eq('status', 'sent')
      .filter('metadata->>notification_type', 'eq', notificationType)
      .eq('id', communicationId)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to load stored email: ${error.message}`);
    }
    if (!data?.content) {
      throw new Error('Stored HTML not available for this row');
    }

    const attempt = toSendAttempt(data);
    return {
      id: data.id,
      subject: data.subject ?? null,
      html: data.content,
      toEmails: attempt.toEmails,
      ccEmails: attempt.ccEmails,
      sentAt: attempt.sentAt,
      status: data.status ?? null,
      context: attempt.context
    };
  }

  /**
   * Send disclosure unsigned reminder email
   * Uses dedicated edge function (not send-notification)
   * For borrower-facing disclosure reminder emails
   */
  async sendDisclosureReminder(params: {
    borrowerEmail: string
    loanOfficerEmail?: string
    borrowerFirstName: string
    borrowerLastName: string
    loanNumber: string
    loanOfficerName: string
    consumerLink: string
    propertyAddress: string
    isTest?: boolean
  }): Promise<{ success: boolean; emailId?: string; error?: string }> {
    try {
      log.info('Sending disclosure reminder', {
        borrowerEmail: params.borrowerEmail,
        loanNumber: params.loanNumber,
        isTest: params.isTest
      });

      const { data, error } = await supabase.functions.invoke('send-disclosure-reminder', {
        body: params
      });

      if (error) {
        log.error('Disclosure reminder failed', {
          loanNumber: params.loanNumber,
          error: error.message
        }, error);
        return { success: false, error: error.message };
      }

      if (!data?.success) {
        const errorMsg = data?.error || data?.reason || 'Unknown error';
        log.warn('Disclosure reminder not sent', {
          loanNumber: params.loanNumber,
          reason: errorMsg,
          skipped: data?.skipped
        });
        return { success: false, error: errorMsg };
      }

      log.info('Disclosure reminder sent successfully', {
        loanNumber: params.loanNumber,
        emailId: data?.emailId
      });

      return {
        success: true,
        emailId: data?.emailId
      };
    } catch (error) {
      log.error('Error sending disclosure reminder', {
        loanNumber: params.loanNumber,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
}

// Export singleton instance
export const notificationSettingsService = new NotificationSettingsService();
